import { useContext, useEffect, useState } from 'react';
import { Badge, Button, Form, Modal, Table } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import { BiPencil, BiPlus, BiTrash } from 'react-icons/bi';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LoaderContext } from '../../../App';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';
import { LS_AUTHORITY_KEY } from '../../../core/constants/common';
import useHasPermission from '../../../core/hooks/useHasPermission';
import GHGEmissionMasterDataService from '../../../core/service/GHGEmissionMasterDataService';
import EmissionDatasetVersionFormModal from './EmissionDatasetVersionFormModal';

const EmissionDatasetVersionManagement = ({ t }) => {
  const lsData = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
  const { setLoaderDisplay } = useContext(LoaderContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { datasetName: paramDatasetName } = useParams();
  const { isSuperAdmin } = useHasPermission();

  const getCompanyId = () => {
    if (isSuperAdmin()) {
      return null;
    }
    return lsData?.profile?.company?.id || null;
  };

  const companyId = getCompanyId();
  const isCompanyAdmin = companyId > 0;

  const [datasets, setDatasets] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState(paramDatasetName || '');
  const [versions, setVersions] = useState([]);
  const [cloneVersions, setCloneVersions] = useState([]);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [selectedSourceVersion, setSelectedSourceVersion] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [versionToDelete, setVersionToDelete] = useState(null);
  const [showVersionFormModal, setShowVersionFormModal] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);

  useEffect(() => {
    loadDatasets();
  }, []);

  useEffect(() => {
    if (selectedDataset) {
      loadVersions();
      // Update URL if dataset changes
      if (selectedDataset !== paramDatasetName) {
        const basePath = isCompanyAdmin ? '/company_admin/ghg' : '/admin';
        const routePath = isCompanyAdmin
          ? `${basePath}/datasets/${selectedDataset}/versions`
          : `${basePath}/ghg_datasets/${selectedDataset}/versions`;
        navigate(routePath, { replace: true });
      }
    }
  }, [selectedDataset, paramDatasetName, isCompanyAdmin, navigate]);

  const loadDatasets = () => {
    setLoaderDisplay(true);
    GHGEmissionMasterDataService.listDatasets()
      .then((response) => {
        if (response.status === 200) {
          const datasetsList = response.data || [];
          setDatasets(datasetsList);
          if (datasetsList.length > 0 && !selectedDataset) {
            const firstDataset = datasetsList[0].name || datasetsList[0];
            setSelectedDataset(firstDataset);
          }
        }
      })
      .catch((error) => {
      })
      .finally(() => {
        setLoaderDisplay(false);
      });
  };

  const loadVersions = () => {
    if (!selectedDataset) return;

    setLoaderDisplay(true);
    GHGEmissionMasterDataService.listVersions(selectedDataset, companyId)
      .then((response) => {
        if (response.status === 200) {
          const versionsList = Array.isArray(response.data) ? response.data : [response.data].filter(Boolean);
          setVersions(versionsList);
        }
      })
      .catch((error) => {
      })
      .finally(() => {
        setLoaderDisplay(false);
      });
  };

  const loadVersionsForClone = () => {
    if (!selectedDataset) return;

    setLoaderDisplay(true);
    // Always fetch without companyId for cloning (superadmin versions only)
    GHGEmissionMasterDataService.listVersions(selectedDataset, null)
      .then((response) => {
        if (response.status === 200) {
          const versionsList = Array.isArray(response.data) ? response.data : [response.data].filter(Boolean);
          setCloneVersions(versionsList);
        }
      })
      .catch((error) => {
      })
      .finally(() => {
        setLoaderDisplay(false);
      });
  };

  const handleCloneVersion = () => {
    if (!selectedSourceVersion) {
      toast.error(t("EmissionDatasetVersionManagement.selectSourceVersion.label"), {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    setLoaderDisplay(true);
    GHGEmissionMasterDataService.cloneVersion(selectedDataset, selectedSourceVersion, companyId)
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          toast.success(t("EmissionDatasetVersionManagement.cloneSuccess.label"), {
            position: toast.POSITION.TOP_RIGHT,
          });
          setShowCloneModal(false);
          setSelectedSourceVersion('');
          loadVersions();
        }
      })
      .catch((error) => {
      })
      .finally(() => {
        setLoaderDisplay(false);
      });
  };

  const handleDeleteVersion = (version) => {
    setVersionToDelete(version);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = (confirmed) => {
    if (!confirmed || !versionToDelete) {
      setShowDeleteModal(false);
      setVersionToDelete(null);
      return;
    }

    setLoaderDisplay(true);
    GHGEmissionMasterDataService.deleteVersion(selectedDataset, versionToDelete.versionNumber, companyId)
      .then((response) => {
        if (response.status === 200 || response.status === 204) {
          toast.success(t("EmissionDatasetVersionManagement.deleteSuccess.label"), {
            position: toast.POSITION.TOP_RIGHT,
          });
          loadVersions();
        }
      })
      .catch((error) => {
      })
      .finally(() => {
        setLoaderDisplay(false);
        setShowDeleteModal(false);
        setVersionToDelete(null);
      });
  };

  const handleEditVersion = (version) => {
    setSelectedVersion(version);
    setShowVersionFormModal(true);
  };

  const handleAddVersion = () => {
    setSelectedVersion(null);
    setShowVersionFormModal(true);
  };

  const handleVersionFormSuccess = () => {
    setShowVersionFormModal(false);
    setSelectedVersion(null);
    loadVersions();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  return (
    <div className="versions-management" data-testid="versions-management">
      <div className="mb-3">
        <h5 className="MH mb-0 text-start">{t("EmissionDatasetVersionManagement.title.label")}</h5>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <Button
            variant="outline-secondary"
            onClick={() => {
              // const basePath = isCompanyAdmin ? '/company_admin/ghg' : '/admin';
              navigate(`../datasets`);
              const basePath = isCompanyAdmin ? '/company_admin/ghg' : '/admin';
              const routePath = isCompanyAdmin
                ? `${basePath}/datasets`
                : `${basePath}/ghg_datasets`;
              navigate(routePath, { replace: true });
            }}
            data-testid="back-button"
          >
            {t("Back.label")}
          </Button>
          <Form.Select
            value={selectedDataset}
            onChange={(e) => setSelectedDataset(e.target.value)}
            style={{ width: 'auto', maxWidth: '200px' }}
            className="d-inline-block"
            data-testid="dataset-selector"
          >
            <option value="">{t("GHGDatasetSelection.selectDataset.label")}</option>
            {datasets.map((dataset) => (
              <option key={dataset.name || dataset} value={dataset.name || dataset}>
                {dataset.name || dataset}
              </option>
            ))}
          </Form.Select>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="primary"
            onClick={handleAddVersion}
            data-testid="new-version-button"
          >
            <BiPlus className="me-1" />
            {t("EmissionDatasetVersionManagement.newVersion.label")}
          </Button>
          <Button
            variant="outline-primary"
            onClick={() => {
              setSelectedSourceVersion('');
              loadVersionsForClone();
              setShowCloneModal(true);
            }}
            data-testid="clone-version-button"
          >
            {t("EmissionDatasetVersionManagement.cloneVersion.label")}
          </Button>
        </div>
      </div>

      {selectedDataset ? (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>{t("EmissionDatasetVersionManagement.table.versionNumber.label")}</th>
              <th>{t("EmissionDatasetVersionManagement.table.versionName.label")}</th>
              <th>{t("EmissionDatasetVersionManagement.table.description.label")}</th>
              <th>{t("EmissionDatasetVersionManagement.table.createdBy.label")}</th>
              <th>{t("EmissionDatasetVersionManagement.table.createdDate.label")}</th>
              <th>{t("EmissionDatasetVersionManagement.table.status.label")}</th>
              <th>{t("EmissionDatasetVersionManagement.table.actions.label")}</th>
            </tr>
          </thead>
          <tbody>
            {versions.length > 0 ? (
              versions.map((version) => (
                <tr key={version.id}>
                  <td>{version.versionNumber}</td>
                  <td>{version.versionName || '-'}</td>
                  <td>{version.description || '-'}</td>
                  <td>{version.createdBy || '-'}</td>
                  <td>{formatDate(version.createdDate)}</td>
                  <td>
                    <Badge bg={version.active ? "success" : "secondary"}>
                      {version.active ? t("Active.label") : t("Inactive.label")}
                    </Badge>
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleEditVersion(version)}
                        data-testid={`edit-version-${version.id}`}
                      >
                        <BiPencil />
                      </Button>
                      {version.versionNumber !== 1 && (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeleteVersion(version)}
                          data-testid={`delete-version-${version.id}`}
                        >
                          <BiTrash />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="text-center">
                  {t("EmissionDatasetVersionManagement.noVersions.label")}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      ) : (
        <div className="text-center p-4">
          <p>{t("EmissionDatasetVersionManagement.selectDataset.label")}</p>
        </div>
      )}

      <Modal show={showCloneModal} onHide={() => {
        setShowCloneModal(false);
        setSelectedSourceVersion('');
      }} centered data-testid="clone-version-modal">
        <Modal.Header closeButton>
          <Modal.Title>{t("EmissionDatasetVersionManagement.cloneModal.title.label")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>{t("EmissionDatasetVersionManagement.cloneModal.selectVersion.label")}</Form.Label>
            <Form.Select
              value={selectedSourceVersion}
              onChange={(e) => setSelectedSourceVersion(e.target.value)}
              data-testid="clone-version-select"
            >
              <option value="">{t("EmissionDatasetVersionManagement.cloneModal.selectVersion.placeholder.label")}</option>
              {cloneVersions.map((version) => (
                <option key={version.versionNumber} value={version.versionNumber}>
                  {version.versionName || `Version ${version.versionNumber}`}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowCloneModal(false);
            setSelectedSourceVersion('');
          }}>
            {t("Cancel.label")}
          </Button>
          <Button variant="primary" onClick={handleCloneVersion} disabled={!selectedSourceVersion}>
            {t("EmissionDatasetVersionManagement.cloneModal.confirm.label")}
          </Button>
        </Modal.Footer>
      </Modal>

      {showDeleteModal && versionToDelete && (
        <ConfirmationModal
          modalShow={true}
          messageText={t("EmissionDatasetVersionManagement.deleteModal.message.label").replace('{{versionName}}', versionToDelete.versionName || versionToDelete.versionNumber || '')}
          callbackModal={handleDeleteConfirm}
          title={t("EmissionDatasetVersionManagement.deleteModal.title.label")}
          confirmBtn={t("Delete.label")}
          CancelBtn={t("Cancel.label")}
        />
      )}

      {showVersionFormModal && (
        <EmissionDatasetVersionFormModal
          show={showVersionFormModal}
          onHide={() => {
            setShowVersionFormModal(false);
            setSelectedVersion(null);
          }}
          datasetName={selectedDataset}
          version={selectedVersion}
          onSuccess={handleVersionFormSuccess}
          companyId={companyId}
        />
      )}
    </div>
  );
};

export default withTranslation()(EmissionDatasetVersionManagement);
